const HOUR_MS = 60 * 60 * 1000;

function timestampOf(photo) {
  const parsed = Date.parse(photo?.created_date || photo?.created_at || "");
  return Number.isFinite(parsed) ? parsed : 0;
}

export function getMomentEngagementScore(photo, now = Date.now()) {
  const storedScore = Number(photo?.engagement_score || 0);
  const weightedScore =
    Number(photo?.heart_count || 0) * 3
    + Number(photo?.funny_count || 0) * 2
    + Number(photo?.shocked_count || 0) * 2
    + Number(photo?.favorite_count || 0) * 4
    + Math.min(Number(photo?.view_count || 0), 500) * 0.25
    + Number(photo?.share_count || 0) * 3;
  const ageHours = Math.max(0, (now - timestampOf(photo)) / HOUR_MS);
  const recencyBoost = Math.max(0, 8 - ageHours / 6);
  return Math.max(storedScore, weightedScore) + recencyBoost;
}

export function rankMoments(
  photos,
  { mode = "live", pickedIds = [], favoriteIds = [], limit, now = Date.now() } = {},
) {
  const picks = new Set(pickedIds);
  const favorites = new Set(favoriteIds);
  let candidates = [...(photos || [])];

  if (mode === "favorites") {
    candidates = candidates.filter((photo) => favorites.has(photo.id));
  }

  if (mode === "live" || mode === "favorites") {
    candidates.sort((left, right) => timestampOf(right) - timestampOf(left));
    return typeof limit === "number" ? candidates.slice(0, limit) : candidates;
  }

  const ranked = candidates
    .map((photo) => ({
      ...photo,
      moment_score: getMomentEngagementScore(photo, now) + (picks.has(photo.id) ? 1000 : 0),
    }))
    .sort((left, right) => {
      if (right.moment_score !== left.moment_score) return right.moment_score - left.moment_score;
      return timestampOf(right) - timestampOf(left);
    });

  if (mode !== "bestof") {
    return typeof limit === "number" ? ranked.slice(0, limit) : ranked;
  }

  const target = typeof limit === "number" ? Math.max(0, limit) : ranked.length;
  const perContributorLimit = Math.max(2, Math.ceil(target * 0.25));
  const contributorCounts = new Map();
  const selected = [];
  const deferred = [];

  ranked.forEach((photo) => {
    const contributor = String(photo.uploader_email || photo.uploader_name || photo.id).trim().toLowerCase();
    const count = contributorCounts.get(contributor) || 0;
    if (selected.length < target && (picks.has(photo.id) || count < perContributorLimit)) {
      selected.push(photo);
      contributorCounts.set(contributor, count + 1);
    } else {
      deferred.push(photo);
    }
  });

  if (selected.length < target) {
    selected.push(...deferred.slice(0, target - selected.length));
  }

  return selected.slice(0, target);
}

export function getEventMomentPhase(event, now = Date.now()) {
  const parseBoundary = (value, endOfDay = false) => {
    const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return Date.parse(value || "");
    const [, year, month, day] = match;
    return Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      endOfDay ? 23 : 0,
      endOfDay ? 59 : 0,
      endOfDay ? 59 : 0,
      endOfDay ? 999 : 0,
    );
  };
  const start = parseBoundary(event?.date, false);
  const end = parseBoundary(event?.end_date || event?.date, true);
  if (Number.isFinite(end) && end < now) return "memories";
  if (Number.isFinite(start) && start <= now) return "live";
  return "upcoming";
}
